// client/src/components/influencer/project/modals/ResultSubmitModal.js
import React, { useState, useEffect } from 'react';

function ResultSubmitModal({ open, onClose, onOpenConfirmModal, project }) {
  const [urlInput, setUrlInput] = useState('');
  const [urls, setUrls] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [note, setNote] = useState('');

  // 모달이 열릴 때 배경 스크롤 막기
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open || !project) return null;

  // URL 추가
  const handleAddUrl = () => {
    const trimmedUrl = urlInput.trim();
    
    if (!trimmedUrl) {
      alert('URL을 입력해주세요.');
      return;
    }

    if (urls.length >= 100) {
      alert('URL은 최대 100개까지 추가할 수 있습니다.');
      return;
    }

    setUrls([...urls, trimmedUrl]);
    setUrlInput('');
  };

  // URL 삭제
  const handleRemoveUrl = (index) => {
    setUrls(urls.filter((_, i) => i !== index));
  };

  // 파일 선택
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = [...selectedFiles, ...files];

    // 파일 개수 체크
    if (newFiles.length > 20) {
      alert('이미지는 최대 20개까지 첨부할 수 있습니다.');
      return;
    }

    // 전체 크기 체크
    const totalSize = newFiles.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > 500 * 1024 * 1024) {
      alert('전체 파일 크기가 500MB를 초과합니다.');
      return;
    }

    // 파일 형식 체크
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
    const invalidFile = newFiles.find(f => !allowedTypes.includes(f.type));
    if (invalidFile) {
      alert(`${invalidFile.name}: 허용되지 않는 파일 형식입니다.\n(png, jpg, jpeg, webp, pdf만 가능)`);
      return;
    }

    setSelectedFiles(newFiles);
  };

  // 파일 삭제
  const handleRemoveFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // 파일 크기 포맷
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // 제출 처리
  const handleSubmit = () => {
    // 확인 모달 열기 (File 객체 그대로 전달)
    onClose(); // 현재 모달 닫기
    onOpenConfirmModal({
      urls,
      files: selectedFiles, // ✅ File 객체 전달
      note
    });
  };

  const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="influencer-result-modal">
      <div className="influencer-result-modal__content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
        {/* X 닫기 버튼 */}
        <button 
          className="influencer-result-modal__close" 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            padding: '0',
            margin: '0',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '26px',
            color: '#9ca3af',
            lineHeight: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s',
            zIndex: 10
          }}
          onMouseEnter={(e) => e.target.style.color = '#374151'}
          onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
        >
          <span style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>×</span>
        </button>

        {/* Header */}
        <div className="influencer-result-modal__header">
          <h2 className="influencer-result-modal__title">결과물 제출</h2>
        </div>

        <div className="influencer-result-modal__body">
          {/* 프로젝트 정보 */}
          <div className="result-submit__project-info">
            <div style={{ fontSize: 14, color: '#6b7280', fontWeight: 600 }}>프로젝트 #{project.projectNumber}</div>
          </div>

          {/* 1. 콘텐츠 URL */}
          <div className="result-submit__field">
            <label className="result-submit__label">
              🔗 콘텐츠를 게시한 URL <span style={{ fontSize: 14, color: '#9ca3af', fontWeight: 400 }}>(선택)</span>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                className="result-submit__input"
                placeholder="https://..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddUrl()}
              />
              <button
                style={{ padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                onClick={handleAddUrl}
                disabled={!urlInput.trim()}
              >
                추가
              </button>
            </div>

            {/* 추가된 URL 목록 */}
            {urls.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {urls.map((url, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                    <div style={{ flex: 1, fontSize: 14, color: '#374151', wordBreak: 'break-all' }}>{url}</div>
                    <button
                      style={{ background: 'none', border: 'none', fontSize: 20, color: '#ef4444', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
                      onClick={() => handleRemoveUrl(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280', textAlign: 'right' }}>{urls.length} / 100개</div>
          </div>

          {/* 2. 이미지 첨부 */}
          <div className="result-submit__field">
            <label className="result-submit__label">
              📷 리뷰 증명 사진 자료 <span style={{ fontSize: 14, color: '#9ca3af', fontWeight: 400 }}>(선택)</span>
            </label>
            <input
              type="file"
              multiple
              accept=".png,.jpg,.jpeg,.webp,.pdf"
              onChange={handleFileSelect}
              style={{ width: '100%', padding: '10px 12px', border: '2px dashed #d1d5db', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
            />

            {/* 선택된 파일 목록 */}
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedFiles.map((file, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                    <div style={{ flex: 1, fontSize: 14, color: '#374151', wordBreak: 'break-all' }}>
                      {file.name} ({formatFileSize(file.size)})
                    </div>
                    <button
                      style={{ background: 'none', border: 'none', fontSize: 20, color: '#ef4444', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
                      onClick={() => handleRemoveFile(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
              총 {formatFileSize(totalSize)} / 500MB ({selectedFiles.length} / 20개)
            </div>
          </div>

          {/* 3. 기타 사항 */}
          <div className="result-submit__field">
            <label className="result-submit__label">
              📝 기타 사항 <span style={{ fontSize: 14, color: '#9ca3af', fontWeight: 400 }}>(선택)</span>
            </label>
            <textarea
              className="result-submit__textarea"
              placeholder="추가로 전달하실 내용이 있다면 입력해주세요."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              rows={5}
            />
            <div className="result-submit__hint" style={{ textAlign: 'right' }}>{note.length} / 2000자</div>
          </div>

          {/* 안내 문구 */}
          <div className="result-submit__warning" style={{ marginBottom: 0 }}>
            💡 위 항목들은 모두 선택사항입니다. 제출 후에는 수정할 수 없습니다.
          </div>
        </div>

        {/* 버튼 */}
        <div className="influencer-result-modal__footer">
          <button
            className="influencer-result-modal__button influencer-result-modal__button--cancel"
            onClick={onClose}
          >
            취소
          </button>
          <button
            className="influencer-result-modal__button influencer-result-modal__button--submit"
            onClick={handleSubmit}
          >
            제출하기
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResultSubmitModal;

